/**
 * Suppliers — the directory of businesses a Company sources hired-in Equipment
 * from (#333).
 *
 * Supplier CRUD used to live three levels deep inside the equipment edit form,
 * as a form nested in a form. It is a Company-wide directory, not a property of
 * one item, so it belongs here; the equipment detail page only LINKS suppliers
 * to an item. Shape follows the vehicles group: an "Add supplier" action that
 * expands an inline panel, and rows with pencil (edit in place) and trash.
 */
import { useEffect, useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { PencilIcon, TrashIcon } from "../../components/ui/icons";
import forms from "../../components/ui/field.module.css";
import { cx } from "@shared/cx";
import { validateContactFields } from "../../components/admin/validators";
import { errorMessage } from "../../lib/use-mutation-runner";
import {
  createSupplier,
  deleteSupplier,
  listSuppliers,
  updateSupplier,
  type Supplier,
  type SupplierCreate,
} from "../../lib/suppliers-api";
import { BackLink, Loading, SectionHeader } from "./section-chrome";
import { InlineBanner } from "../../components/ui/InlineBanner";
import chrome from "./settings.module.css";

const emptyDraft: SupplierCreate = { name: "", email: null, phone: null };

export function SuppliersSection({ onBack }: { onBack?: () => void } = {}) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SupplierCreate>(emptyDraft);
  const [saving, setSaving] = useState(false);
  // The panel's own failure sink: a rejected save renders inside the form,
  // never at the section top (DESIGN-SYSTEM.md error placement).
  const [saveError, setSaveError] = useState<string | null>(null);
  // Reveal-then-live: hidden until the first save attempt on this panel.
  const [showValidation, setShowValidation] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Supplier | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fieldErrors = validateContactFields({
    name: draft.name,
    email: draft.email ?? "",
    phone: draft.phone ?? "",
  });

  // Deletion is a soft delete (suppliers.active = false); the list endpoint
  // returns active rows only, so a deleted supplier drops out on refresh.
  const refresh = async (signal?: AbortSignal) => {
    try {
      const rows = await listSuppliers(signal);
      setSuppliers(rows);
      setError(null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(errorMessage(err));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    void refresh(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPanel = (supplier: Supplier | null) => {
    setEditId(supplier?.id ?? null);
    setDraft(
      supplier
        ? { name: supplier.name, email: supplier.email, phone: supplier.phone }
        : emptyDraft,
    );
    setShowValidation(false);
    setSaveError(null);
    setFormOpen(true);
  };

  const save = async () => {
    if (Object.keys(fieldErrors).length > 0) {
      setShowValidation(true);
      return;
    }
    setSaving(true);
    try {
      const payload: SupplierCreate = {
        name: draft.name.trim(),
        email: draft.email?.trim() || null,
        phone: draft.phone?.trim() || null,
      };
      if (editId) {
        await updateSupplier({ id: editId, ...payload });
      } else {
        await createSupplier(payload);
      }
      setFormOpen(false);
      setSaveError(null);
      await refresh();
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSupplier(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteError(null);
      await refresh();
    } catch (err) {
      setDeleteError(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className={chrome.section}>
      {onBack && <BackLink label="All operations" onClick={onBack} />}
      <SectionHeader
        title="Suppliers"
        description="Businesses you hire equipment in from. Link one to an item on that item's page."
        action={
          <Button size="sm" variant="secondary" onClick={() => openPanel(null)}>
            Add supplier
          </Button>
        }
      />
      <div>
        {error && (
          <p role="alert" className={chrome.sectionError}>
            {error}
          </p>
        )}

        {loading ? (
          <Loading />
        ) : suppliers.length === 0 ? (
          <p className={chrome.empty}>
            No suppliers yet. Add the businesses you hire gear in from.
          </p>
        ) : (
          <ul className={chrome.list}>
            {suppliers.map((s) => (
              <li key={s.id} className={chrome.row}>
                <div className={chrome.rowText}>
                  <p className={chrome.rowName}>{s.name}</p>
                  <p className={chrome.rowMeta}>
                    {[s.email, s.phone].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <div className={chrome.rowActions}>
                  <button
                    type="button"
                    aria-label={`Edit ${s.name}`}
                    className={chrome.iconButton}
                    onClick={() => openPanel(s)}
                  >
                    <PencilIcon className={chrome.icon} />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${s.name}`}
                    className={chrome.iconButton}
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteTarget(s);
                    }}
                  >
                    <TrashIcon className={chrome.icon} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* The form sits under the list: a create appends a row there, and
            the panel stays in one place whichever action opened it. */}
        {formOpen && (
          <div className={chrome.panel}>
            <div className={cx(chrome.grid, chrome.gridThree)}>
              <div>
                <label className={forms.label} htmlFor="supplier-name">
                  Name
                </label>
                <input
                  id="supplier-name"
                  autoFocus
                  className={cx(
                    forms.input,
                    showValidation && fieldErrors.name && forms.invalid,
                  )}
                  value={draft.name}
                  aria-invalid={
                    (showValidation && Boolean(fieldErrors.name)) || undefined
                  }
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                {showValidation && fieldErrors.name && (
                  <InlineBanner variant="error">
                    {fieldErrors.name}
                  </InlineBanner>
                )}
              </div>
              <div>
                <label className={forms.label} htmlFor="supplier-email">
                  Email <span className={forms.optional}>(optional)</span>
                </label>
                <input
                  id="supplier-email"
                  type="email"
                  className={cx(
                    forms.input,
                    showValidation && fieldErrors.email && forms.invalid,
                  )}
                  value={draft.email ?? ""}
                  aria-invalid={
                    (showValidation && Boolean(fieldErrors.email)) || undefined
                  }
                  onChange={(e) =>
                    setDraft({ ...draft, email: e.target.value })
                  }
                />
                {showValidation && fieldErrors.email && (
                  <InlineBanner variant="error">
                    {fieldErrors.email}
                  </InlineBanner>
                )}
              </div>
              <div>
                <label className={forms.label} htmlFor="supplier-phone">
                  Phone <span className={forms.optional}>(optional)</span>
                </label>
                <input
                  id="supplier-phone"
                  type="tel"
                  className={cx(
                    forms.input,
                    showValidation && fieldErrors.phone && forms.invalid,
                  )}
                  value={draft.phone ?? ""}
                  aria-invalid={
                    (showValidation && Boolean(fieldErrors.phone)) || undefined
                  }
                  onChange={(e) =>
                    setDraft({ ...draft, phone: e.target.value })
                  }
                />
                {showValidation && fieldErrors.phone && (
                  <InlineBanner variant="error">
                    {fieldErrors.phone}
                  </InlineBanner>
                )}
              </div>
            </div>
            {saveError && (
              <Banner variant="error" className={chrome.bannerInPanel}>
                {saveError}
              </Banner>
            )}
            <div className={chrome.panelActions}>
              <Button size="sm" loading={saving} onClick={() => void save()}>
                {editId ? "Save supplier" : "Add supplier"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <ConfirmModal
          open={deleteTarget !== null}
          title="Delete supplier"
          message={`Delete "${deleteTarget?.name ?? ""}"? Items hired in from them lose the link.`}
          confirmLabel="Delete"
          loading={deleting}
          error={deleteError}
          onConfirm={() => void remove()}
          onCancel={() => setDeleteTarget(null)}
        />
      </div>
    </section>
  );
}
